import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Input, { InputType } from 'components/shared/Input'
import { useIntl } from 'react-intl'
import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { CardBoxField, setCardDetails } from 'store/unlimited-slice'

import { useErrorMessages } from 'utils/hooks/useErrorMessages'
import { useAutoScroll } from 'utils/hooks'
import Info from 'components/shared/Info'
import moment from 'moment'

interface CardOwnerDataProps {
    index: number
}

const CardOwnerData = ({ index }: CardOwnerDataProps) => {
    const { formatMessage } = useIntl()
    const dispatch = useAppDispatch()
    const { cardBoxes } = useAppSelector((state) => state.unlimited)
    const { handleRemoveError, getError } = useErrorMessages()
    const { animatePendingScroll } = useAutoScroll()

    const [dateOfBirth, setDateOfBirth] = useState('')

    const { bundleData, cartItemId } = cardBoxes[index]
    const isCardBundled = !!bundleData
    const bundledCards = cardBoxes.filter((item) => item.bundleData)
    const standardCards = cardBoxes.filter((item) => !item.bundleData)
    const groupIndex = isCardBundled
        ? bundledCards.findIndex((item) => item.cartItemId === cartItemId)
        : standardCards.findIndex((item) => item.cartItemId === cartItemId)

    const handleBirthDateChange = (date: string) => {
        const isoDate = moment(date, 'DD-MM-YYYY').format('YYYY-MM-DD')

        setDateOfBirth(date)
        dispatch(
            setCardDetails({
                field: 'dateOfBirth',
                value: isoDate,
                index,
            })
        )
    }

    const setFieldValue = (field: CardBoxField, value: string) => {
        dispatch(setCardDetails({ field, value, index }))
    }

    const calculateId = (fieldName: string) =>
        `${
            isCardBundled ? 'itemsBundle' : 'items'
        }[${groupIndex}].detail.${fieldName}`

    const isWrongPersonTypeBackendError =
        getError(calculateId('dateOfBirth')) === 'Wrong person type'

    const renderField = ({
        id,
        name,
        label,
        type,
        autofill,
    }: {
        id: string
        name: CardBoxField
        label: string
        placeholder?: string
        type?: InputType
        autofill?: string
    }) => (
        <Input
            type={type || 'text'}
            id={id}
            label={label}
            placeholder=""
            autofill={autofill}
            maxWidth={347}
            isRequired
            value={cardBoxes[index][name]}
            isError={!!getError(id)}
            errorMessage={getError(id)}
            onBlur={(value) => setFieldValue(name, value)}
            onChange={(value) => {
                handleRemoveError(id)
                setFieldValue(name, value)
            }}
            testId={`unlimited-${index}-field-${name}`}
        />
    )

    useEffect(() => {
        animatePendingScroll(`cardOwnerData${index}`)
    }, [])

    useEffect(() => {
        const currentValue = cardBoxes[index].dateOfBirth
        currentValue &&
            handleBirthDateChange(
                moment(currentValue, 'YYYY-MM-DD').format('DD-MM-YYYY')
            )
    }, [])

    return (
        <Box
            id={`cardOwnerData${index}`}
            gap={24}
            mb={[32, 64]}
            mt={[32, 64]}
            isFullWidth
        >
            <Text variant="heading3">
                {formatMessage({
                    id: 'annual.owner_data.title',
                    defaultMessage: 'Podaj dane właściciela karty',
                })}
            </Text>
            <Box gap={[16, 24]} isFullWidth>
                {renderField({
                    id: calculateId('firstName'),
                    name: 'firstName',
                    label: formatMessage({
                        id: 'global.input.first_name_label',
                        defaultMessage: 'Imię',
                    }),
                    type: 'text',
                    autofill: 'given-name',
                })}

                {renderField({
                    id: calculateId('lastName'),
                    name: 'lastName',
                    label: formatMessage({
                        id: 'global.input.last_name_label',
                        defaultMessage: 'Nazwisko',
                    }),
                    type: 'text',
                    autofill: 'family-name',
                })}

                <Input
                    type="text"
                    id={calculateId('dateOfBirth')}
                    label={formatMessage({
                        id: 'global.input.birth_date',
                        defaultMessage: 'Data urodzenia',
                    })}
                    mask="##-##-####"
                    onChange={(value) => {
                        handleBirthDateChange(value)
                        handleRemoveError(calculateId('dateOfBirth'))
                    }}
                    value={dateOfBirth}
                    // onBlur={() => handleCheckDateOnBlur()}
                    maskPlaceholder={'_'}
                    maxWidth={347}
                    isRequired
                    isError={
                        !!getError(calculateId('dateOfBirth'))
                        // || isDateAbsurd
                    }
                    errorMessage={
                        isWrongPersonTypeBackendError
                            ? ''
                            : getError(calculateId('dateOfBirth'))
                    }
                    inputMode="numeric"
                    testId={`unlimited-${index}-field-dateOfBirth`}
                />
                {isWrongPersonTypeBackendError && (
                    // || isDateAbsurd
                    <Info color={'error'}>
                        <Text variant="bodySmall" color="error">
                            {formatMessage(
                                {
                                    id: 'annual.birth_date_error_info',
                                    defaultMessage:
                                        'Data urodzenia nie zgadza się z wybranym typem gościa. Sprawdź datę urodzenia lub <a>wybierz inny typ gościa.</a>',
                                    description:
                                        'Keep the html tags in the translation',
                                },
                                {
                                    // eslint-disable-next-line id-length
                                    a: (chunks: string[]) => (
                                        <Text
                                            as="a"
                                            variant="bodySmall"
                                            color="error"
                                            isUnderlined
                                            href={`#cardPersonType${index}`}
                                        >
                                            {chunks}
                                        </Text>
                                    ),
                                }
                            )}
                        </Text>
                    </Info>
                )}

                {renderField({
                    id: calculateId('email'),
                    name: 'email',
                    label: formatMessage({
                        id: 'global.input.email_label',
                        defaultMessage: 'Adres e-mail',
                    }),
                    type: 'email',
                    autofill: 'email',
                })}

                {renderField({
                    id: calculateId('phone'),
                    name: 'phone',
                    label: formatMessage({
                        id: 'global.input.phone_label',
                        defaultMessage: 'Numer telefonu',
                    }),
                    type: 'tel',
                    autofill: 'tel',
                })}
            </Box>
        </Box>
    )
}
export default CardOwnerData
